<template>
  <div class="kds-order-card card border-0 shadow-sm" :class="'state-' + order.state">

    <!-- Card Header -->
    <div class="kds-card-header px-3 py-2">
      <!-- Primary row: order no. + type | elapsed -->
      <div class="d-flex justify-content-between align-items-center gap-2">
        <div class="d-flex align-items-center gap-2 min-w-0">
          <span class="kds-session-badge" v-if="order.session_no">
            <i class="fa-solid fa-receipt me-1" style="font-size: 0.65rem;" />#{{ sessionNoDisplay }}
          </span>
          <span class="kds-type-badge" :class="'kds-type-badge--' + (order.type || 'dine_in')" v-if="typeMeta">
            <i :class="typeMeta.icon" class="me-1" style="font-size: 0.7rem;" />{{ $t(typeMeta.label) }}
          </span>
        </div>
        <span class="kds-elapsed flex-shrink-0" :class="{ 'text-danger fw-bold': isLate }">
          <i class="fa-regular fa-clock me-1"></i>{{ elapsedTime }}
        </span>
      </div>
      <!-- Secondary row: table · customer · queue -->
      <div class="kds-card-subhead d-flex align-items-center gap-2 flex-wrap mt-1">
        <span class="kds-meta" v-if="order.table_name">
          <i class="fa-solid fa-chair" />{{ order.table_name }}
        </span>
        <span class="kds-meta" v-if="order.customer_name">
          <i class="fa-solid fa-user" />{{ order.customer_name }}
        </span>
        <span class="kds-meta" v-if="!order.customer_name && !order.table_name">
          <i class="fa-solid fa-person-walking" />{{ $t('Walk-in') }}
        </span>
        <span class="kds-meta kds-meta--queue ms-auto" v-if="order.queue_no" :title="$t('Kitchen queue')">
          <i class="fa-solid fa-list-ol" />Q{{ order.queue_no }}
        </span>
      </div>
    </div>

    <!-- Items List -->
    <div class="card-body px-3 py-2">
      <div
        v-for="item in order.items"
        :key="item.id + '-' + (item.updated_flash || 0)"
        class="kds-item d-flex align-items-start gap-2 py-2"
        :class="{
          'kds-item--clickable': isItemClickable(),
          'kds-item--done': (order.state === 'progressing' && item.state === 'ready')
            || (order.state === 'ready' && item.state === 'completed'),
          'kds-item--updated': !!item.updated_flash
        }"
        :role="isItemClickable() ? 'button' : undefined"
        @click="onItemClick(item)"
      >
        <span class="kds-qty-badge flex-shrink-0">x{{ item.quantity }}</span>
        <div class="flex-grow-1 min-w-0">
          <div class="kds-item-name fw-bold lh-sm d-flex align-items-center gap-1 flex-wrap" style="font-size: 0.92rem;">
            <span class="badge bg-warning text-dark" v-if="item.is_reward">
              <i class="fa-solid fa-gift me-1" style="font-size: 0.7rem;" />{{ $t('Reward') }}
            </span>
            <span class="badge kds-updated-badge" v-if="item.updated_flash">
              <i class="fa-solid fa-pen me-1" style="font-size: 0.65rem;" />{{ $t('updated') }}
            </span>
            <span>{{ $ln(item, 'product_name') }}</span>
            <i v-if="(order.state === 'progressing' && item.state === 'ready') || (order.state === 'ready' && item.state === 'completed')" class="fa-solid fa-circle-check text-success" style="font-size: 0.82rem;"></i>
            <span
              v-if="item.ingredients && item.ingredients.length > 0"
              class="kds-ing-trigger ms-auto"
              @mouseenter="showIngPopover($event, item)"
              @mouseleave="hideIngPopover"
              @click.stop="showIngPopover($event, item)"
            >
              <i class="fa-solid fa-wheat-awn"></i>
            </span>
          </div>
          <div v-if="groupedModifiers(item).length > 0" class="kds-modifier-groups mt-1">
            <div
              v-for="(grp, gi) in groupedModifiers(item)"
              :key="gi"
              class="kds-modifier-row"
            >
              <span class="kds-modifier-group-label">{{ $ln(grp, 'group_name') || $t('Options') }}</span>
              <span class="kds-modifier-values">{{ grp.options.map(o => $ln(o)).join(', ') }}</span>
            </div>
          </div>
          <div v-if="item.remark" class="kds-remark mt-1">
            <i class="fa-solid fa-comment-dots me-1"></i>{{ item.remark }}
          </div>
          <div v-if="item.cancelled_quantity > 0" class="kds-cancelled-notice mt-1">
            <span class="kds-cancelled-notice__text">
              <i class="fa-solid fa-ban me-1"></i>
              <span class="fw-bold">{{ $t('Cancelled') }} <s>x{{ item.cancelled_quantity }}</s></span>
              <span v-if="item.cancelled_reason" class="ms-1 opacity-90">— {{ item.cancelled_reason }}</span>
              <span v-if="item.restocked_quantity > 0" class="ms-1 opacity-90">
                · {{ $t('Restocked') }} {{ item.restocked_quantity }}/{{ item.cancelled_quantity }}
              </span>
            </span>
            <button
              v-if="restockableQty(item) > 0"
              type="button"
              class="kds-cancelled-notice__action"
              :title="$t('Restock Ingredients')"
              :aria-label="$t('Restock Ingredients')"
              @click.stop="confirmRestock(item)"
            >
              <i class="fa-solid fa-rotate-left"></i>
              <span class="kds-cancelled-notice__action-label">{{ $t('Restock') }}</span>
            </button>
            <span
              v-else
              class="kds-cancelled-notice__done"
              :title="$t('Already restocked')"
            >
              <i class="fa-solid fa-check"></i>
              <span class="kds-cancelled-notice__action-label">{{ $t('Restocked') }}</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Button -->
    <div class="px-3 pb-3">
      <button
        v-if="order.state === 'queued'"
        class="btn btn-warning text-white fw-bold w-100"
        style="font-size: 0.85rem;"
        @click="$emit('update-state', order.id, 'progressing')"
      >
        <i class="fa-solid fa-fire-burner me-2"></i>{{ $t('Start Cooking') }}
      </button>
      <button
        v-else-if="order.state === 'progressing'"
        class="btn btn-success fw-bold w-100"
        style="font-size: 0.85rem;"
        @click="$emit('update-state', order.id, 'ready')"
      >
        <i class="fa-solid fa-bell-concierge me-2"></i>{{ $t('Mark ALL Ready') }}
      </button>
      <div v-else-if="order.state === 'ready'" class="d-flex gap-2">
        <button
          class="btn btn-outline-warning fw-bold"
          style="font-size: 0.85rem;"
          @click="confirmRevertAll"
        >
          <i class="fa-solid fa-rotate-left"></i>
        </button>
        <button
          class="btn btn-outline-secondary fw-bold flex-grow-1"
          style="font-size: 0.85rem;"
          @click="$emit('update-state', order.id, 'completed')"
        >
          <i class="fa-solid fa-check-double me-2"></i>{{ $t('Complete ALL') }}
        </button>
      </div>
    </div>

    <!-- Ingredients popover (teleported to body so it's not clipped by card) -->
    <teleport to="body">
      <div
        v-if="ingPopover.visible"
        ref="ingPopover"
        class="kds-ing-popover-floating"
        :style="ingPopoverStyle"
        @mouseenter="cancelHideIngPopover"
        @mouseleave="hideIngPopover"
      >
        <div class="kds-ing-popover__title d-flex align-items-center justify-content-between">
          <span><i class="fa-solid fa-bowl-food me-1"></i>{{ $t('Ingredients') }}</span>
          <small class="opacity-75 fw-normal">{{ $t('per dish') }}</small>
        </div>
        <ul class="kds-ing-popover__list">
          <li v-for="(ing, idx) in ingPopover.items" :key="idx">
            <span class="kds-ing-popover__name">
              {{ ing.name }}
              <small v-if="ing.source === 'option'" class="opacity-75">
                ({{ $ln(ing, 'group_name') }}: {{ $ln(ing, 'option_name') }})
              </small>
            </span>
            <span class="kds-ing-popover__qty text-end">
              <span class="d-block">{{ formatQty(ing.quantity) + ' ' + $t(ing.unit) }}</span>
              <small v-if="ingPopover.qty > 1" class="opacity-75 d-block">
                × {{ ingPopover.qty }} = <b>{{ formatQty(ing.quantity * ingPopover.qty) + ' ' + $t(ing.unit) }}</b>
              </small>
            </span>
          </li>
        </ul>
      </div>
    </teleport>

  </div>
</template>

<script>
import { distance } from '@/store/date-fns';

export default {
  name: 'KdsOrderCard',
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  emits: ['update-state', 'update-item-state', 'update-items-state', 'restock-item'],
  data() {
    return {
      ingPopover: {
        visible: false,
        placement: 'bottom',  // 'bottom' = กางลง (ยึด top), 'top' = พลิกขึ้น (ยึด bottom)
        top: 0, bottom: 0, left: 0, width: 280, maxHeight: 0,
        items: [], qty: 1,
      },
      hideIngTimer: null,
      dismissBound: false,
    };
  },
  beforeUnmount() {
    if (this.hideIngTimer) clearTimeout(this.hideIngTimer);
    this.unbindIngDismiss();
  },
  methods: {
    // วาง popover ให้อยู่ในจอเสมอ: กางลงถ้าที่ว่างพอ ไม่พอก็พลิกขึ้น
    // แล้ว cap ความสูงตามที่ว่างจริง (แทน max-height ตายตัว) ให้เหลือ scroll ชั้นเดียว
    showIngPopover(evt, item) {
      this.cancelHideIngPopover();
      const rect = evt.currentTarget.getBoundingClientRect();
      const MARGIN = 8;   // ระยะห่างขอบจอ
      const GAP    = 6;   // ระยะห่างจากไอคอน
      const MIN_H  = 120; // ต่ำกว่านี้ถือว่าที่ไม่พอ

      const width = Math.min(280, window.innerWidth - MARGIN * 2);
      let left = rect.right - width;
      if (left + width > window.innerWidth - MARGIN) left = window.innerWidth - width - MARGIN;
      if (left < MARGIN) left = MARGIN;

      const spaceBelow = window.innerHeight - rect.bottom - GAP - MARGIN;
      const spaceAbove = rect.top - GAP - MARGIN;
      const flipUp = spaceBelow < MIN_H && spaceAbove > spaceBelow;

      this.ingPopover = {
        visible: true,
        placement: flipUp ? 'top' : 'bottom',
        top: rect.bottom + GAP,
        bottom: window.innerHeight - rect.top + GAP,
        left,
        width,
        maxHeight: Math.max(MIN_H, Math.floor(flipUp ? spaceAbove : spaceBelow)),
        items: item.ingredients || [],
        qty: item.quantity || 1,
      };
      this.bindIngDismiss();
    },
    hideIngPopover() {
      this.cancelHideIngPopover();
      // delay เพื่อให้ mouse ย้ายจาก icon ไป popover ได้ทัน (ไม่งั้น popover หายก่อน scroll)
      this.hideIngTimer = setTimeout(() => {
        this.closeIngPopover();
      }, 150);
    },
    closeIngPopover() {
      this.cancelHideIngPopover();
      this.ingPopover.visible = false;
      this.unbindIngDismiss();
    },
    cancelHideIngPopover() {
      if (this.hideIngTimer) {
        clearTimeout(this.hideIngTimer);
        this.hideIngTimer = null;
      }
    },
    // popover เป็น position:fixed ที่คำนวณตำแหน่งครั้งเดียว — ถ้าคอลัมน์ข้างหลังเลื่อน
    // มันจะลอยค้างหลุดจากไอคอน จึงปิดทิ้งเมื่อมี scroll/resize นอกตัว popover เอง
    bindIngDismiss() {
      if (this.dismissBound) return;
      this.onOutsideScroll = (e) => {
        const el = this.$refs.ingPopover;
        if (el && e.target instanceof Node && el.contains(e.target)) return;
        this.closeIngPopover();
      };
      this.onOutsidePointer = (e) => {
        if (e.target.closest && e.target.closest('.kds-ing-trigger')) return;
        const el = this.$refs.ingPopover;
        if (el && el.contains(e.target)) return;
        this.closeIngPopover();
      };
      window.addEventListener('scroll', this.onOutsideScroll, true);
      window.addEventListener('resize', this.closeIngPopover);
      document.addEventListener('pointerdown', this.onOutsidePointer, true);
      this.dismissBound = true;
    },
    unbindIngDismiss() {
      if (!this.dismissBound) return;
      window.removeEventListener('scroll', this.onOutsideScroll, true);
      window.removeEventListener('resize', this.closeIngPopover);
      document.removeEventListener('pointerdown', this.onOutsidePointer, true);
      this.dismissBound = false;
    },
    groupedModifiers(item) {
      const mods = item.modifiers || [];
      if (!mods.length) return [];
      const map = new Map();
      mods.forEach(m => {
        const key = m.group_name || '';
        if (!map.has(key)) map.set(key, { group_name: key, group_name_en: m.group_name_en || '', options: [] });
        map.get(key).options.push(m);
      });
      return Array.from(map.values());
    },
    formatQty(q) {
      const n = Number(q) || 0;
      // Trim trailing zeros, keep up to 2 decimals
      return (Math.round(n * 100) / 100).toString();
    },
    isItemClickable() {
      // Progressing card: กดได้ทุก item (mark ready หรือ undo)
      // Ready card: กดได้ทุก item (mark completed หรือ undo)
      return this.order.state === 'progressing' || this.order.state === 'ready';
    },
    onItemClick(item) {
      if (!this.isItemClickable(item)) return;

      if (this.order.state === 'progressing') {
        if (item.state !== 'ready') {
          // progressing → ready
          this.$emit('update-item-state', this.order.id, item.id, 'ready');
        } else {
          // undo: ready → progressing (confirm)
          this.confirmRevert(item, 'progressing');
        }
      } else if (this.order.state === 'ready') {
        if (item.state !== 'completed') {
          // ready → completed
          this.$emit('update-item-state', this.order.id, item.id, 'completed');
        } else {
          // undo: completed → ready (confirm)
          this.confirmRevert(item, 'ready');
        }
      }
    },
    confirmRevert(item, revertTo) {
      const messages = {
        progressing: this.$t('Move back to cooking queue'),
        ready:       this.$t('Move back to ready queue'),
      };
      this.$swal({
        icon: 'warning',
        iconColor: '#d97706',
        title: this.$t('Undo') + '?',
        html: '<b>' + this.$ln(item, 'product_name') + '</b><br>' + messages[revertTo],
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: '<i class="fa-solid fa-rotate-left me-1"></i>' + this.$t('Undo'),
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-warning text-white me-2',
          cancelButton: 'btn btn-secondary',
        },
        focusConfirm: false,
      }).then((result) => {
        if (result.isConfirmed) {
          this.$emit('update-item-state', this.order.id, item.id, revertTo);
        }
      });
    },
    restockableQty(item) {
      const cancelled = parseInt(item.cancelled_quantity) || 0;
      const restocked = parseInt(item.restocked_quantity) || 0;
      return Math.max(0, cancelled - restocked);
    },
    renderRestockIngredients(item, qty) {
      if (!(item.ingredients || []).length) return '';
      const rows = item.ingredients
        .filter(ing => ing.source === 'product' || ing.source === 'option')
        .map(ing => {
          const baseQty = ing.quantity * qty;
          const qtyStr = (Math.round(baseQty * 100) / 100).toString();
          const label = ing.source === 'option'
            ? `${ing.name} <small class="opacity-75">(${ing.group_name}: ${ing.option_name})</small>`
            : ing.name;
          return `<li>${label} — <b>${qtyStr} ${ing.unit}</b></li>`;
        })
        .join('');
      return '<ul class="text-start small ps-3 mt-2 mb-0">' + rows + '</ul>';
    },
    confirmRestock(item) {
      const maxQty = this.restockableQty(item);
      if (maxQty <= 0) return;

      const headerHtml =
        '<div class="fw-bold mb-1">' + item.product_name
        + ' <s class="text-danger">x' + item.cancelled_quantity + '</s></div>'
        + (item.cancelled_reason
            ? '<div class="text-muted small mb-2">' + item.cancelled_reason + '</div>'
            : '')
        + (item.restocked_quantity > 0
            ? '<div class="text-muted small mb-2">'
                + this.$t('Already restocked') + ': '
                + '<b>' + item.restocked_quantity + '/' + item.cancelled_quantity + '</b>'
              + '</div>'
            : '');

      const baseOpts = {
        icon: 'question',
        iconColor: '#16a34a',
        title: this.$t('Restock Ingredients') + '?',
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: '<i class="fa-solid fa-rotate-left me-1"></i>' + this.$t('Confirm Restock'),
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-success me-2',
          cancelButton: 'btn btn-secondary',
        },
        focusConfirm: false,
      };

      if (maxQty === 1) {
        // Single unit: simple confirm, no input
        this.$swal({
          ...baseOpts,
          html:
            headerHtml
            + '<div class="small">' + this.$t('The following ingredients will be returned to stock:') + '</div>'
            + this.renderRestockIngredients(item, 1),
        }).then((result) => {
          if (result.isConfirmed) {
            this.$emit('restock-item', this.order.id, item.id, 1);
          }
        });
        return;
      }

      // Multi-unit: stepper input + live-updating ingredient preview
      let currentQty = maxQty;
      this.$swal({
        ...baseOpts,
        html:
          headerHtml
          + '<div class="small mb-2">' + this.$t('Restock Quantity')
          + ' <span class="text-muted">(' + this.$t('Max') + ': ' + maxQty + ')</span></div>'
          + '<div class="d-flex align-items-center justify-content-center gap-3 mb-2">'
            + '<button id="kds-restock-minus" type="button" class="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center" style="width:44px;height:44px;">'
              + '<i class="fa-solid fa-minus"></i>'
            + '</button>'
            + '<span id="kds-restock-qty" class="fw-bold fs-2" style="min-width:60px;text-align:center;">' + currentQty + '</span>'
            + '<button id="kds-restock-plus" type="button" class="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center" style="width:44px;height:44px;" disabled>'
              + '<i class="fa-solid fa-plus"></i>'
            + '</button>'
          + '</div>'
          + '<div class="small mt-2">' + this.$t('The following ingredients will be returned to stock:') + '</div>'
          + '<div id="kds-restock-ings">' + this.renderRestockIngredients(item, currentQty) + '</div>',
        didOpen: () => {
          const qtyEl    = document.getElementById('kds-restock-qty');
          const ingEl    = document.getElementById('kds-restock-ings');
          const minusBtn = document.getElementById('kds-restock-minus');
          const plusBtn  = document.getElementById('kds-restock-plus');

          const sync = () => {
            qtyEl.textContent = currentQty;
            ingEl.innerHTML   = this.renderRestockIngredients(item, currentQty);
            minusBtn.disabled = currentQty <= 1;
            plusBtn.disabled  = currentQty >= maxQty;
          };
          minusBtn.addEventListener('click', () => {
            if (currentQty > 1) { currentQty--; sync(); }
          });
          plusBtn.addEventListener('click', () => {
            if (currentQty < maxQty) { currentQty++; sync(); }
          });
          sync();
        },
        preConfirm: () => currentQty,
      }).then((result) => {
        if (result.isConfirmed) {
          this.$emit('restock-item', this.order.id, item.id, result.value);
        }
      });
    },
    confirmRevertAll() {
      const readyItems = this.order.items.filter(i => (i.state || this.order.state) === 'ready');
      const names = readyItems.map(i => this.$ln(i, 'product_name')).join('<br>');
      this.$swal({
        icon: 'warning',
        iconColor: '#d97706',
        title: this.$t('Undo') + '?',
        html: names + '<br><br>' + this.$t('Move back to cooking queue'),
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: '<i class="fa-solid fa-rotate-left me-1"></i>' + this.$t('Undo'),
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-warning text-white me-2',
          cancelButton: 'btn btn-secondary',
        },
        focusConfirm: false,
      }).then((result) => {
        if (result.isConfirmed) {
          // One bulk request instead of looping per item.
          this.$emit('update-items-state', this.order.id, readyItems.map(i => i.id), 'progressing');
        }
      });
    }
  },
  computed: {
    ingPopoverStyle() {
      const p = this.ingPopover;
      const style = {
        left: p.left + 'px',
        width: p.width + 'px',
        maxHeight: p.maxHeight + 'px',
      };
      if (p.placement === 'top') style.bottom = p.bottom + 'px';
      else style.top = p.top + 'px';
      return style;
    },
    typeMeta() {
      const map = {
        dine_in:  { icon: 'fa-solid fa-utensils',     label: 'Dine-in'  },
        takeaway: { icon: 'fa-solid fa-bag-shopping', label: 'Takeaway' },
        delivery: { icon: 'fa-solid fa-motorcycle',   label: 'Delivery' },
      };
      return map[this.order.type] || map.dine_in;
    },
    sessionNoDisplay() {
      if (!this.order.session_no) return '';
      return String(this.order.session_no).padStart(4, '0');
    },
    elapsedTime() {
      return this.order.created_at ? distance(this.order.created_at) : '-';
    },
    isLate() {
      if (!this.order.created_at) return false;
      const diffMs = Date.now() - new Date(this.order.created_at).getTime();
      return diffMs > 10 * 60 * 1000; // over 10 min
    }
  }
}
</script>


